import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Chip,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { issueService } from '../services/issueService';
import { releasesAPI } from '../services/api';
import IssueList from '../components/IssueList';
import IssueDetail from '../components/IssueDetail';
import { useToast } from '../context/ToastContext';

const Issues = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [releases, setReleases] = useState([]);
    const [selectedReleaseId, setSelectedReleaseId] = useState('all');
    const { showSuccess, showError } = useToast();

    // URL parameters for deep linking
    const urlReleaseId = searchParams.get('release');
    const urlIssueId = searchParams.get('issue');
    const urlStatus = searchParams.get('status');
    const urlPriority = searchParams.get('priority');
    const urlScope = searchParams.get('scope');

    // Fetch releases and auto-select current ongoing release
    useEffect(() => {
        const fetchReleases = async () => {
            try {
                const data = await releasesAPI.getAll();
                setReleases(data);

                // If URL has a release param, use that
                if (urlReleaseId) {
                    setSelectedReleaseId(parseInt(urlReleaseId));
                    return;
                }

                // Auto-select: prefer in_progress release, else closest future release_date
                const now = new Date();
                let bestRelease = null;
                let bestDiff = Infinity;

                for (const rel of data) {
                    if (rel.overall_status === 'in_progress') {
                        bestRelease = rel;
                        break;
                    }
                    if (rel.release_date) {
                        const relDate = new Date(rel.release_date);
                        const diff = relDate - now;
                        const absDiff = diff >= 0 ? diff : Math.abs(diff) + 1e12;
                        if (absDiff < bestDiff) {
                            bestDiff = absDiff;
                            bestRelease = rel;
                        }
                    }
                }

                if (bestRelease) {
                    setSelectedReleaseId(bestRelease.id);
                }
            } catch (error) {
                console.error('Error fetching releases:', error);
            }
        };
        fetchReleases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReleaseChange = (event) => {
        const value = event.target.value;
        setSelectedReleaseId(value);
        const newParams = new URLSearchParams(searchParams);
        if (value === 'all') {
            newParams.delete('release');
        } else {
            newParams.set('release', value.toString());
        }
        setSearchParams(newParams);
    };

    const handleCreateClick = () => {
        setSelectedIssue(null);
        setOpenDetail(true);
    };

    const handleEditClick = (issue) => {
        setSelectedIssue(issue);
        setOpenDetail(true);
    };

    const handleCloseDetail = () => {
        setOpenDetail(false);
        setSelectedIssue(null);
    };

    const handleSaveIssue = async (issueData, mediaFiles) => {
        try {
            if (selectedIssue) {
                await issueService.update(selectedIssue.id, issueData);
                if (mediaFiles && (mediaFiles.video || (mediaFiles.screenshots && mediaFiles.screenshots.length > 0))) {
                    const formData = new FormData();
                    if (mediaFiles.video) formData.append('files', mediaFiles.video);
                    if (mediaFiles.screenshots?.length > 0) {
                        mediaFiles.screenshots.forEach((file) => formData.append('files', file));
                    }
                    await issueService.uploadMedia(selectedIssue.id, formData);
                }
                showSuccess('Issue updated successfully');
            } else {
                const createdIssue = await issueService.create(issueData);
                if (mediaFiles && (mediaFiles.video || (mediaFiles.screenshots && mediaFiles.screenshots.length > 0))) {
                    const formData = new FormData();
                    if (mediaFiles.video) formData.append('files', mediaFiles.video);
                    if (mediaFiles.screenshots?.length > 0) {
                        mediaFiles.screenshots.forEach((file) => formData.append('files', file));
                    }
                    await issueService.uploadMedia(createdIssue.id, formData);
                }
                showSuccess('Issue created successfully');
            }
            handleCloseDetail();
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Error saving issue:', error);
            showError('Failed to save issue: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDeleteIssue = () => {
        setRefreshTrigger(prev => prev + 1);
        showSuccess('Issue deleted successfully');
    };

    const effectiveReleaseId = selectedReleaseId === 'all' ? null : selectedReleaseId;
    const selectedReleaseName = releases.find(r => r.id === selectedReleaseId)?.version;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header Row */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" component="h1">
                    Issue Tracker
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreateClick}
                    >
                        New Issue
                    </Button>
                </Box>
            </Box>

            {/* Release Selector */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel id="release-selector-label">Release Version</InputLabel>
                        <Select
                            labelId="release-selector-label"
                            value={selectedReleaseId}
                            label="Release Version"
                            onChange={handleReleaseChange}
                        >
                            <MenuItem value="all">
                                <em>All Releases</em>
                            </MenuItem>
                            {releases.map((rel) => (
                                <MenuItem key={rel.id} value={rel.id}>
                                    {rel.version}
                                    {rel.name ? ` — ${rel.name}` : ''}
                                    {rel.overall_status === 'in_progress' ? ' (Active)' : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedReleaseName && (
                        <Chip
                            label={`Showing: ${selectedReleaseName}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            onDelete={() => {
                                setSelectedReleaseId('all');
                                const newParams = new URLSearchParams(searchParams);
                                newParams.delete('release');
                                setSearchParams(newParams);
                            }}
                        />
                    )}
                </Box>
            </Paper>

            {/* Issue List (includes its own IssueTrackerSummary tiles) */}
            <Paper elevation={2} sx={{ p: 2 }}>
                <IssueList
                    onEdit={handleEditClick}
                    onDelete={handleDeleteIssue}
                    refreshTrigger={refreshTrigger}
                    defaultReleaseId={effectiveReleaseId}
                    autoExpandIssueId={urlIssueId ? parseInt(urlIssueId) : null}
                    defaultStatus={urlStatus || null}
                    defaultPriority={urlPriority || null}
                    defaultScope={urlScope || null}
                />
            </Paper>

            {/* Issue Detail Modal */}
            <IssueDetail
                open={openDetail}
                onClose={handleCloseDetail}
                onSave={handleSaveIssue}
                issue={selectedIssue}
                defaultReleaseId={effectiveReleaseId}
            />
        </Container>
    );
};

export default Issues;
